import { Box, Card, Group, Stack, Text } from "@mantine/core";
import type { ReactNode } from "react";
import { STREAM, type StreamKey } from "../../theme/mantine-theme";
import { Metric } from "./Metric";

/**
 * A dashboard stream tile: one card per stream, all of equal weight.
 *
 * Equal weight is the design decision, not an accident of layout. The three
 * streams are three different people's whole day, and making one bigger would
 * be the app deciding whose work matters most.
 */
export function StreamCard({
  title,
  stream,
  badge,
  hero,
  heroLabel,
  supporting = [],
  footer,
  onClick,
}: {
  title: string;
  /**
   * A stream token. StreamKey rather than StateKey, so a status token -
   * "svcCollected" - cannot be passed here: a stream is a part of the
   * business, not a state a record is in.
   */
  stream: StreamKey;
  /** Optional right-hand warning, e.g. "3 overdue". */
  badge?: ReactNode;
  hero: string | number;
  heroLabel?: string;
  supporting?: { value: string | number; label: string }[];
  footer?: ReactNode;
  onClick?: () => void;
}) {
  const token = STREAM[stream];
  return (
    <Card
      withBorder
      radius="md"
      padding="lg"
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : undefined }}
    >
      <Group justify="space-between" align="center" mb="md" wrap="nowrap">
        <Group gap="sm" wrap="nowrap">
          {/* The stream dot. Decorative - the title beside it is what names
              the stream, so a reader who cannot see the colour loses nothing. */}
          <Box
            aria-hidden
            w={8}
            h={8}
            style={{
              borderRadius: "50%",
              backgroundColor: `var(--mantine-color-${token.color}-${token.shade})`,
              flexShrink: 0,
            }}
          />
          <Text fz="lg" fw={600} c="gray.9">
            {title}
          </Text>
        </Group>
        {badge}
      </Group>

      <Metric value={hero} label={heroLabel} size="hero" />

      {supporting.length > 0 && (
        <Group gap="xl" mt="md" wrap="wrap">
          {supporting.map((item) => (
            <Metric
              key={item.label}
              value={item.value}
              label={item.label}
              size="inline"
            />
          ))}
        </Group>
      )}

      {footer && (
        <Stack gap={0} mt="md">
          <Text fz="sm" c="gray.6">
            {footer}
          </Text>
        </Stack>
      )}
    </Card>
  );
}
